/**
 * Each integration's health (section 17.2; ADR 0034): a status in words and
 * a colored dot, what needs a look, and the facts behind it. Each card opens
 * collapsed, to its status (ADR 0119). The server reads what the worker's
 * jobs recorded; this only shows it. Refreshed every minute.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { ApiError } from "../../api/client";
import type {
  AutotaskHealth,
  BackupHealth,
  DattoHealth,
  HealthLevel,
  HeartbeatHealth,
  Integrations,
  JobRun,
  Microsoft365Health,
  NetworkHealth,
  ScreenConnectHealth,
  SkySwitchHealth,
  VpnHealth,
  WorkerHealth,
} from "../../api/types";
import { WRITE_MODE_TEXT } from "../../api/write-mode";
import { useSession } from "../../app/session-context";
import { ChevronRightIcon } from "../../components/ui/Icons";
import { Panel } from "../../components/ui/Panel";
import { formatBytes } from "../../lib/bytes";
import { formatDateTimeIn, formatLongDay, isIsoDay, todayIn } from "../../lib/dates";
import { possessive } from "../../lib/possessive";

export const REFRESH_MS = 60_000;

const LEVELS: Record<HealthLevel, { label: string; dot: string }> = {
  ok: { label: "Healthy", dot: "bg-status-logged-fg" },
  warning: { label: "Needs a look", dot: "bg-status-needs-client-fg" },
  failing: { label: "Failing", dot: "bg-now" },
  not_run: { label: "Not run yet", dot: "bg-muted" },
  not_set_up: { label: "Not set up here", dot: "bg-muted" },
};

// What each of this environment's SkySwitch subscriptions carries.
const MODEL_LABELS: Record<string, string> = {
  cdr: "Call records",
  call: "Live calls",
};

function when(iso: string | null, timeZone: string, never = "Never"): string {
  return iso === null ? never : formatDateTimeIn(iso, timeZone);
}

/** Which answering rule SkySwitch said was in effect on each extension with
 * timed rules, at the worker's hourly read (ADR 0151). */
function TimeFrames({ health, timeZone }: { health: SkySwitchHealth; timeZone: string }) {
  const read = health.phone_schedule?.last_success_at ?? null;
  if (read === null) {
    return <>Not read yet</>;
  }
  if (health.time_frames.length === 0) {
    return <>No extension has timed answering rules · read {when(read, timeZone)}</>;
  }
  return (
    <>
      <ul>
        {health.time_frames.map((f) => (
          <li key={f.extension}>{`Extension ${f.extension}: ${f.time_frame ?? "Default"}`}</li>
        ))}
      </ul>
      <span className="mt-1 block text-muted">Read {when(read, timeZone)}, hourly.</span>
    </>
  );
}

function expiry(value: string | null): string {
  if (value === null) {
    return "no expiry given";
  }
  const day = value.slice(0, 10);
  return isIsoDay(day) ? `expires ${formatLongDay(day)}` : `expires ${value}`;
}

function Level({ level, id }: { level: HealthLevel; id?: string }) {
  const { label, dot } = LEVELS[level];
  return (
    <span id={id} className="flex shrink-0 items-center gap-2 text-sm font-semibold">
      <span aria-hidden="true" className={`size-2.5 rounded-pill ${dot}`} />
      {label}
    </span>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-row bg-panel-2 px-3 py-2.5">
      <dt className="text-xs font-semibold text-muted">{term}</dt>
      <dd className="text-[13.5px]">{children}</dd>
    </div>
  );
}

function LastError({
  run,
  timeZone,
  term = "Last error",
}: {
  run: JobRun | null;
  timeZone: string;
  term?: string;
}) {
  if (run?.last_error == null || run.last_error_at === null) {
    return null;
  }
  return (
    <Fact term={term}>
      <span className="font-mono text-[12.5px] break-words">
        {run.last_error}
      </span>
      <span className="text-muted">{` · ${formatDateTimeIn(run.last_error_at, timeZone)}`}</span>
    </Fact>
  );
}

/**
 * One integration's card (Idea #416; ADR 0119). It opens collapsed: one row,
 * the same height for every card, with the status in the upper corner. The
 * row is a button that shows the problems and the facts below it.
 */
function IntegrationPanel({
  id,
  name,
  level,
  problems,
  children,
}: {
  id: string;
  name: string;
  level: HealthLevel;
  problems: string[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // What the button shows: the problems, if any, and the facts.
  const shown = problems.length > 0 ? `${id}-problems ${id}-facts` : `${id}-facts`;
  return (
    <Panel aria-labelledby={id} className="flex min-w-0 flex-1 flex-col gap-3">
      <h2 className="text-lg font-semibold tracking-tight">
        <button
          type="button"
          // Its name and its status: "Autotask Healthy".
          aria-labelledby={`${id} ${id}-level`}
          aria-expanded={open}
          aria-controls={open ? shown : undefined}
          className={
            "flex w-full cursor-pointer items-center justify-between gap-3 rounded-row text-left " +
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink"
          }
          onClick={() => {
            setOpen(!open);
          }}
        >
          <span className="flex items-center gap-2">
            <span
              className={
                "flex text-muted transition-transform motion-reduce:transition-none " + (open ? "rotate-90" : "")
              }
            >
              <ChevronRightIcon />
            </span>
            <span id={id}>{name}</span>
          </span>
          {/* A space between, unseen in the flex row, so its text reads "Autotask Healthy" as its name does (WCAG 2.5.3). */}{" "}
          <Level level={level} id={`${id}-level`} />
        </button>
      </h2>
      {open && problems.length > 0 ? (
        <ul id={`${id}-problems`} aria-label={`${name} problems`} className="flex flex-col gap-1">
          {problems.map((problem) => (
            <li key={problem} className="text-[13.5px] font-medium">
              {problem}
            </li>
          ))}
        </ul>
      ) : null}
      {open ? (
        <dl id={`${id}-facts`} className="grid gap-2 min-[700px]:grid-cols-2">
          {children}
        </dl>
      ) : null}
    </Panel>
  );
}

type Read = AutotaskHealth["time_entries"];
type FailedSubmission = AutotaskHealth["failed_submissions"][number];

/** The day view with the failed submission's block selected, on the tech's own day. */
function blockLink(failed: FailedSubmission): string {
  const params = new URLSearchParams({ user: failed.user_id, date: failed.day, block: failed.block_id });
  return `/?${params.toString()}`;
}

/** One of the worker's regular Autotask reads (ADR 0063): what it last found, and when. */
function ReadFacts({
  term,
  read,
  timeZone,
}: {
  term: string;
  read: Read;
  timeZone: string;
}) {
  return (
    <>
      <Fact term={term}>
        {read === null || read.run.last_success_at === null
          ? "Not read yet"
          : `${read.found === null ? "Read" : `${read.found.toLocaleString("en-US")} found`} · ${when(read.run.last_success_at, timeZone)}`}
      </Fact>
      <LastError
        run={read?.run ?? null}
        timeZone={timeZone}
        term={`${term} error`}
      />
    </>
  );
}

function Autotask({
  health,
  timeZone,
}: {
  health: AutotaskHealth;
  timeZone: string;
}) {
  const counts = Object.entries(health.counts);
  return (
    <IntegrationPanel
      id="health-autotask"
      name="Autotask"
      level={health.level}
      problems={health.problems}
    >
      <Fact term="Last successful sync">
        {when(health.sync?.last_success_at ?? null, timeZone)}
      </Fact>
      <Fact term="Last attempt">
        {when(health.sync?.last_attempt_at ?? null, timeZone)}
      </Fact>
      <LastError run={health.sync} timeZone={timeZone} />
      <Fact term="API usage">
        {health.usage_pct === null
          ? "Not recorded yet"
          : `${health.usage_pct.toFixed(1)}% of the hourly threshold` +
            (health.usage_at === null
              ? ""
              : `, at ${when(health.usage_at, timeZone)}`)}
      </Fact>
      {counts.length > 0 ? (
        <Fact term="Synced">
          {counts
            .map(([entity, n]) => `${entity} ${n.toLocaleString("en-US")}`)
            .join(" · ")}
        </Fact>
      ) : null}
      {health.warnings.length > 0 ? (
        <Fact term="Warnings">
          <ul>
            {health.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Fact>
      ) : null}
      <ReadFacts
        term="Time entries"
        read={health.time_entries}
        timeZone={timeZone}
      />
      <ReadFacts
        term="Open tickets"
        read={health.open_tickets}
        timeZone={timeZone}
      />
      {/* AUTOTASK_WRITE_MODE with the owner's switch applied (ADR 0052). */}
      <Fact term="Writes to Autotask">
        {WRITE_MODE_TEXT[health.write_mode]}
      </Fact>
      {health.unset_settings.length > 0 ? (
        <Fact term="Not chosen yet">{health.unset_settings.join(", ")}</Fact>
      ) : null}
      {health.failed_submissions.length > 0 ? (
        <Fact term="Failed submissions">
          {/* Section 10.6: Try again is on the block itself (ADR 0053), so each
              links to the block on the tech's own day (Idea #289). */}
          <ul aria-label="Failed submissions" className="flex flex-col gap-1.5">
            {health.failed_submissions.map((failed) => (
              <li key={failed.submission_id}>
                <Link
                  to={blockLink(failed)}
                  className="block font-medium text-brand-ink hover:underline"
                >
                  {[
                    failed.client ?? "No client",
                    failed.tech ?? "Unknown tech",
                    when(failed.failed_at, timeZone),
                    failed.ticket_id === null
                      ? null
                      : `ticket ${failed.ticket_number ?? String(failed.ticket_id)}`,
                  ]
                    .filter((part) => part !== null)
                    .join(" · ")}
                </Link>
                <span className="block text-muted">{failed.error ?? "No reason was recorded."}</span>
              </li>
            ))}
          </ul>
        </Fact>
      ) : null}
    </IntegrationPanel>
  );
}

type FailedRecord = SkySwitchHealth["failed_records"][number];

/** Failed inbox records, each with Retry, and Retry all (ADR 0034 section 5):
 * SkySwitch's, and ScreenConnect's since #307. */
function FailedRecords({
  term,
  records,
  more = 0,
  retryWith,
  timeZone,
}: {
  term: string;
  records: readonly FailedRecord[];
  /** How many more failed than are listed. */
  more?: number;
  retryWith: (ids?: string[]) => Promise<{ retried: number }>;
  timeZone: string;
}) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: retryWith,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["integrations"] }),
  });
  const button =
    "h-8 cursor-pointer rounded-pill border border-line bg-card px-3 text-[13px] font-medium hover:bg-brand-tint disabled:opacity-50";
  if (records.length === 0) {
    return null;
  }
  return (
    <Fact term={term}>
      <ul aria-label={term} className="flex flex-col gap-1.5">
        {records.map((record) => (
          <li
            key={record.id}
            className="flex items-center justify-between gap-2"
          >
            <span>
              {`${formatDateTimeIn(record.received_at, timeZone)} · ${record.channel === "poll" ? "history" : "pushed"} · ${String(record.attempts)} ${record.attempts === 1 ? "try" : "tries"}`}
              {record.last_error ? (
                <span className="block font-mono text-[12px] text-muted">
                  {record.last_error}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              className={button}
              disabled={retry.isPending}
              onClick={() => {
                retry.mutate([record.id]);
              }}
            >
              Retry
            </button>
          </li>
        ))}
      </ul>
      {more > 0 ? (
        <span className="block text-muted">{`and ${more.toLocaleString("en-US")} more`}</span>
      ) : null}
      <button
        type="button"
        className={`${button} mt-2`}
        disabled={retry.isPending}
        onClick={() => {
          retry.mutate(undefined);
        }}
      >
        Retry all
      </button>
      {retry.isError ? (
        <p
          role="alert"
          className="mt-1 text-[13px] text-status-needs-client-fg"
        >
          Couldn&apos;t retry:{" "}
          {retry.error instanceof ApiError
            ? (retry.error.detail ?? retry.error.message)
            : "try again"}
        </p>
      ) : null}
    </Fact>
  );
}

function SkySwitch({
  health,
  timeZone,
}: {
  health: SkySwitchHealth;
  timeZone: string;
}) {
  const { api } = useSession();
  const { inbox } = health;
  return (
    <IntegrationPanel
      id="health-skyswitch"
      name="SkySwitch"
      level={health.level}
      problems={health.problems}
    >
      {health.set_up ? (
        <>
          <Fact term="Subscription">
            {health.subscriptions.length === 0 ? (
              "None for this environment"
            ) : (
              <ul>
                {health.subscriptions.map((s) => (
                  <li key={s.subscription_id}>
                    {s.model ? `${MODEL_LABELS[s.model] ?? s.model}: ` : null}
                    <span className="font-mono text-[12.5px]">
                      {s.subscription_id}
                    </span>
                    {` · ${expiry(s.expires)}`}
                  </li>
                ))}
              </ul>
            )}
          </Fact>
          <Fact term="Last checked">
            {when(health.check?.last_success_at ?? null, timeZone)}
          </Fact>
          <LastError
            run={health.check}
            timeZone={timeZone}
            term="Last check error"
          />
          {health.scope !== null ? (
            <Fact term="API access">
              {[health.scope, health.time_zone].filter(Boolean).join(" · ")}
            </Fact>
          ) : null}
          <Fact term="Rate limit">
            {health.rate_limit === null
              ? "Not recorded yet"
              : `${String(health.rate_limit.remaining)} requests left, resetting in ${String(health.rate_limit.reset_seconds)} s (${health.rate_limit.policy})`}
          </Fact>
          <Fact term="Extension list">
            {health.extensions === null
              ? when(
                  health.directory?.last_success_at ?? null,
                  timeZone,
                  "Not synced yet",
                )
              : `${String(health.extensions)} extensions · ${when(health.directory?.last_success_at ?? null, timeZone)}`}
          </Fact>
          <LastError
            run={health.directory}
            timeZone={timeZone}
            term="Extension list error"
          />
          <Fact term="Last call pushed">
            {when(inbox.last_webhook_at, timeZone)}
          </Fact>
          <Fact term="Time frame in effect">
            <TimeFrames health={health} timeZone={timeZone} />
          </Fact>
          <LastError
            run={health.phone_schedule}
            timeZone={timeZone}
            term="Time frame read error"
          />
          <Fact term="Last history read">
            {health.poll === null
              ? "Not read yet"
              : `${when(health.poll.run.last_success_at, timeZone)}${
                  health.poll.in_window === null
                    ? ""
                    : ` · ${String(health.poll.in_window)} calls, ${String(health.poll.new ?? 0)} new`
                }`}
          </Fact>
          <LastError
            run={health.poll?.run ?? null}
            timeZone={timeZone}
            term="History read error"
          />
          {health.unmapped_extensions.length > 0 ? (
            <Fact term="Extensions on calls with no tech (7 days)">
              <ul>
                {health.unmapped_extensions.map((u) => (
                  <li key={u.extension}>
                    <span className="font-mono">{u.extension}</span>
                    {` · ${String(u.calls)} ${u.calls === 1 ? "call" : "calls"}, last ${formatDateTimeIn(u.last_seen_at, timeZone)}`}
                  </li>
                ))}
              </ul>
              <span className="mt-1 block text-muted">
                A tech&apos;s extension here gets their calls once it&apos;s
                mapped on{" "}
                <Link
                  to="/mappings/techs"
                  className="font-semibold text-brand-ink hover:underline"
                >
                  Mappings → Techs
                </Link>
                . The hunt group stays unmapped.
              </span>
            </Fact>
          ) : null}
          <Fact term="Received records">
            {`Waiting ${String(inbox.pending)} · Processed ${String(inbox.processed)} · Failed ${String(inbox.failed)} · Ignored ${String(inbox.ignored)}`}
            {inbox.oldest_pending_at !== null ? (
              <span className="block text-muted">{`Oldest waiting: ${formatDateTimeIn(inbox.oldest_pending_at, timeZone)}`}</span>
            ) : null}
          </Fact>
          <FailedRecords
            term="Failed records"
            records={health.failed_records}
            retryWith={(ids) => api.retrySkySwitch(ids)}
            timeZone={timeZone}
          />
        </>
      ) : (
        <Fact term="Status">
          SkySwitch isn&apos;t used in this environment.
        </Fact>
      )}
    </IntegrationPanel>
  );
}

/**
 * ScreenConnect's automation pushes (roadmap PR-43; ADRs 0068, 0070 and
 * 0071): whether they arrive and are read, and the names no one has yet.
 */
// Sessions a technician joined that the app never saw (PR-42b): found nightly.
function unseenLine(unseen: number): string {
  if (unseen === 0) return "";
  return ` · ${String(unseen)} ${unseen === 1 ? "session" : "sessions"} the app never saw`;
}

/** How far back a read-back went, when the owner chose a period (ADR 0098): a day otherwise. */
function periodLine(hours: number | undefined): string {
  if (hours === undefined || hours === 24) return "";
  if (hours === 1) return " · over the last hour";
  if (hours % 24 === 0) return ` · over the last ${String(hours / 24)} days`;
  return ` · over the last ${String(hours)} hours`;
}

function ScreenConnect({
  health,
  timeZone,
}: {
  health: ScreenConnectHealth;
  timeZone: string;
}) {
  const { api } = useSession();
  return (
    <IntegrationPanel
      id="health-screenconnect"
      name="ScreenConnect"
      level={health.level}
      problems={health.problems}
    >
      <Fact term="Webhook">
        {health.set_up
          ? "Set up: the token and the secret are set"
          : "Not set up: set WEBHOOK_TOKEN_SCREENCONNECT and WEBHOOK_SECRET_SCREENCONNECT (portainer.md)"}
      </Fact>
      <Fact term="Last push">
        {when(health.last_push_at, timeZone, "None yet")}
      </Fact>
      <Fact term="Pushes in the last 24 hours">
        {health.pushes_24h.toLocaleString("en-US")}
      </Fact>
      <Fact term="Waiting to be read">
        {health.waiting === 0
          ? "None"
          : `${health.waiting.toLocaleString("en-US")}: read every minute`}
        {health.oldest_waiting_at !== null ? (
          <span className="block text-muted">{`Oldest waiting: ${formatDateTimeIn(health.oldest_waiting_at, timeZone)}`}</span>
        ) : null}
      </Fact>
      <FailedRecords
        term="Couldn't be read"
        records={health.failed_events}
        more={health.failed - health.failed_events.length}
        retryWith={(ids) => api.retryScreenConnect(ids)}
        timeZone={timeZone}
      />
      {health.unmapped_technicians.length > 0 ? (
        <Fact term="Technicians with no ScreenConnect name (7 days)">
          <ul>
            {health.unmapped_technicians.map((u) => (
              <li key={u.name}>
                {`${u.name} · ${String(u.connections)} ${u.connections === 1 ? "connection" : "connections"}, last ${formatDateTimeIn(u.last_seen_at, timeZone)}`}
              </li>
            ))}
          </ul>
          <span className="mt-1 block text-muted">
            Their sessions are on someone&apos;s day once the name is theirs on{" "}
            <Link
              to="/mappings/techs"
              className="font-semibold text-brand-ink hover:underline"
            >
              Mappings → Techs
            </Link>
            .
          </span>
        </Fact>
      ) : null}
      <Fact term="Last reconciliation">
        {health.reconcile === null
          ? "Not run yet. With the worker's ScreenConnect API values set (screenconnect.md step 7), it runs every 15 minutes; Owner tools → ScreenConnect → Missed ScreenConnect sessions runs it now."
          : `${when(health.reconcile.last_success_at, timeZone)}${
              health.reconciled.sessions === undefined
                ? ""
                : ` · ${String(health.reconciled.sessions)} sessions, ${String(health.reconciled.connections ?? 0)} connections read back, ${String(health.reconciled.new ?? 0)} new`
            }${unseenLine(health.reconciled.unseen ?? 0)}${periodLine(health.reconciled.hours)}`}
      </Fact>
      <LastError
        run={health.reconcile}
        timeZone={timeZone}
        term="Reconciliation error"
      />
      <Fact term="Company list">
        {health.companies === null
          ? "Not copied yet. With the worker's ScreenConnect API values set (screenconnect.md step 7), they're copied nightly at 07:22 UTC; Owner tools → ScreenConnect → Copy ScreenConnect's company names copies them now."
          : `${health.companies_listed === null ? "" : `${health.companies_listed.toLocaleString("en-US")} names · `}${when(health.companies.last_success_at, timeZone)}`}
      </Fact>
      <LastError
        run={health.companies}
        timeZone={timeZone}
        term="Company list error"
      />
      <Fact term="Company names to map">
        {health.unmapped_companies === 0 ? (
          "None"
        ) : (
          <>
            {`${health.unmapped_companies.toLocaleString("en-US")} on `}
            <Link
              to="/mappings?screenconnect=unmapped"
              className="font-semibold text-brand-ink hover:underline"
            >
              Mappings → Clients
            </Link>
          </>
        )}
      </Fact>
    </IntegrationPanel>
  );
}

/**
 * Datto RMM (Phase 4; roadmap PR-52; ADR 0080, 0105): sign-in, the budget, any
 * pause, the copies, and the activity log's 10-minute read and nightly check.
 */
function DattoRmm({
  health,
  timeZone,
}: {
  health: DattoHealth;
  timeZone: string;
}) {
  const counts = health.site_counts;
  return (
    <IntegrationPanel
      id="datto-rmm-health"
      name="Datto RMM"
      level={health.level}
      problems={health.problems}
    >
      <Fact term="Sign-in">
        {health.sign_in === null
          ? "Not checked yet. With the worker's Datto values set (datto-rmm.md step 4), it's checked at 17 past each hour; Owner tools → Datto RMM → Check the Datto RMM sign-in checks now."
          : `${when(health.sign_in.last_success_at, timeZone)}${health.account ? ` · ${health.account}` : ""}`}
      </Fact>
      <LastError
        run={health.sign_in}
        timeZone={timeZone}
        term="Sign-in error"
      />
      <Fact term="Requests in the last minute">
        {`${String(health.requests_last_minute)} of ${String(health.budget)}`}
      </Fact>
      <Fact term="Paused">
        {health.paused_until === null
          ? "No"
          : `Until ${when(health.paused_until, timeZone)}, after a ${String(health.paused_by)}`}
      </Fact>
      <Fact term="Sites">
        {health.sites === null
          ? "Not copied yet"
          : `${when(health.sites.last_success_at, timeZone)}${
              counts.sites === undefined
                ? ""
                : ` · ${String(counts.sites)} sites, ${String(counts.linked ?? 0)} linked to clients`
            }`}
      </Fact>
      <LastError run={health.sites} timeZone={timeZone} term="Sites error" />
      <Fact term="Users">
        {health.users === null
          ? "Not copied yet"
          : `${when(health.users.last_success_at, timeZone)}${
              health.users_listed === null
                ? ""
                : ` · ${String(health.users_listed)} users`
            }`}
      </Fact>
      <LastError run={health.users} timeZone={timeZone} term="Users error" />
      <Fact term="Activity log">
        {health.poll === null
          ? "Not read yet"
          : `${when(health.poll.last_success_at, timeZone)}${
              health.poll.last_success_at === null ||
              health.poll_entries === null
                ? ""
                : ` · ${String(health.poll_entries)} ${health.poll_entries === 1 ? "entry" : "entries"} read`
            }`}
      </Fact>
      <LastError
        run={health.poll}
        timeZone={timeZone}
        term="Activity log error"
      />
      <Fact term="Nightly check">
        {health.reconcile === null
          ? "Not run yet"
          : when(health.reconcile.last_success_at, timeZone)}
      </Fact>
      <LastError
        run={health.reconcile}
        timeZone={timeZone}
        term="Nightly check error"
      />
    </IntegrationPanel>
  );
}

type GraphMailbox = Microsoft365Health["mailboxes"][number];
type GraphFolder = GraphMailbox["folders"][number];

// Graph's well-known folder names (ADR 0121), as Outlook shows them.
const FOLDER_LABELS: Record<GraphFolder["folder"], string> = { inbox: "Inbox", sentitems: "Sent Items" };

/** A folder's subscription, in words: its status, and anything Graph asked or missed. */
function folderLine(folder: GraphFolder, timeZone: string): string {
  let status: string;
  switch (folder.status) {
    case null:
      status = "not subscribed yet";
      break;
    case "active":
      status = folder.expires_at === null ? "subscribed" : `subscribed until ${formatDateTimeIn(folder.expires_at, timeZone)}`;
      break;
    case "removed":
      status = "removed by Graph: the next check makes it again";
      break;
    case "refused":
      status = "refused by Exchange";
      break;
    case "failed":
      status = folder.last_error === null ? "failed" : `failed: ${folder.last_error}`;
      break;
  }
  return [
    `${FOLDER_LABELS[folder.folder]}: ${status}`,
    folder.reauthorize_at === null ? null : `Graph asked to reauthorize it ${formatDateTimeIn(folder.reauthorize_at, timeZone)}`,
    folder.missed_at === null ? null : `missed notifications ${formatDateTimeIn(folder.missed_at, timeZone)}`,
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

function Mailboxes({ mailboxes, timeZone }: { mailboxes: readonly GraphMailbox[]; timeZone: string }) {
  if (mailboxes.length === 0) {
    return (
      <Fact term="Mailboxes">
        None switched on: turn each tech&apos;s on in{" "}
        <Link to="/mappings/techs" className="font-semibold text-brand-ink hover:underline">
          Mappings → Techs
        </Link>
        .
      </Fact>
    );
  }
  return (
    <Fact term="Mailboxes">
      <ul aria-label="Mailboxes" className="flex flex-col gap-1.5">
        {mailboxes.map((mailbox) => (
          <li key={mailbox.user_id}>
            <span className="block font-medium">{mailbox.name ?? mailbox.address}</span>
            <span className="block text-muted break-all">{mailbox.address}</span>
            {mailbox.refused_at !== null ? (
              <span className="block text-status-needs-client-fg">Refused: not in the techs&apos; group</span>
            ) : (
              <span className="block text-muted">
                {mailbox.last_seen_at === null ? "No mail seen yet" : `Seen ${formatDateTimeIn(mailbox.last_seen_at, timeZone)}`}
              </span>
            )}
            {mailbox.folders.map((folder) => (
              <span key={folder.folder} className="block">
                {folderLine(folder, timeZone)}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </Fact>
  );
}

/**
 * Microsoft 365 email (Phase 5; roadmap PR-63; ADRs 0109, 0110 and 0121): the
 * daemon's sign-in and certificate, each switched-on mailbox's subscriptions,
 * and what Graph has sent. Where it isn't set up, it says so, and isn't red.
 */
const SEND_MODES: Record<string, string> = {
  off: "Off: EMAIL_SEND_MODE is off, so nothing is sent",
  owners: "Owners only",
  on: "On: owners and techs",
};

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** Email from Axon Time (ADR 0147), as the worker last recorded it. */
function EmailFacts({ email, timeZone }: { email: Microsoft365Health["email"]; timeZone: string }) {
  if (email === null) {
    return (
      <Fact term="Email from Axon Time">
        Not set up yet: set EMAIL_FROM and EMAIL_SEND_MODE on the worker (m365.md step 10). The worker
        says how it&apos;s set up every 15 minutes.
      </Fact>
    );
  }
  const mode = SEND_MODES[email.mode ?? ""] ?? "Not recorded yet";
  const sent = [
    plural(email.reminders, "reminder", "reminders"),
    plural(email.summaries, "summary", "summaries"),
    plural(email.alerts, "alert", "alerts"),
  ];
  const failedLast =
    email.last_error_at !== null && (email.last_sent_at === null || email.last_error_at > email.last_sent_at);
  return (
    <>
      <Fact term="Email from Axon Time">
        {email.sender === null ? `${mode}. EMAIL_FROM isn't set on the worker` : `${mode}, from ${email.sender}`}
      </Fact>
      <Fact term="Last email sent">{when(email.last_sent_at, timeZone, "None yet")}</Fact>
      <Fact term="Sent in the last 7 days">
        {email.reminders + email.summaries + email.alerts === 0 ? "None" : sent.join(", ")}
      </Fact>
      {failedLast && email.last_error_at !== null ? (
        <Fact term="Last email error">{`${formatDateTimeIn(email.last_error_at, timeZone)}: ${email.last_error ?? ""}`}</Fact>
      ) : null}
    </>
  );
}

function Microsoft365({ health, timeZone }: { health: Microsoft365Health; timeZone: string }) {
  return (
    <IntegrationPanel id="health-microsoft-365" name="Microsoft 365" level={health.level} problems={health.problems}>
      {health.set_up ? (
        <>
          <Fact term="Sign-in">
            {health.sign_in === null
              ? "Not checked yet. With the worker's GRAPH_CLIENT_ID and GRAPH_CERTIFICATE set (m365.md step 6), it's checked at 47 past each hour; Owner tools → Microsoft 365 → Check the Microsoft 365 sign-in checks now."
              : when(health.sign_in.last_success_at, timeZone)}
          </Fact>
          <LastError run={health.sign_in} timeZone={timeZone} term="Sign-in error" />
          <Fact term="Certificate expires">{when(health.certificate_expires_at, timeZone, "Not recorded yet")}</Fact>
          <Fact term="Webhook">
            {health.webhook_set_up
              ? "Set up: the token and the clientState are set"
              : "Not set up: set WEBHOOK_TOKEN_MSGRAPH and GRAPH_CLIENT_STATE_SECRET (m365.md step 8)"}
          </Fact>
          <Fact term="Subscriptions checked">
            {health.stopped_at !== null
              ? `Stopped by msgraph_unsubscribe ${formatDateTimeIn(health.stopped_at, timeZone)}, until msgraph_subscribe`
              : health.check === null
                ? "Not run yet"
                : when(health.check.last_success_at, timeZone)}
          </Fact>
          {health.check_again_at !== null && (
            <Fact term="Next check">{`${formatDateTimeIn(health.check_again_at, timeZone)}, as Microsoft asked`}</Fact>
          )}
          <LastError run={health.check} timeZone={timeZone} term="Subscription check error" />
          <Mailboxes mailboxes={health.mailboxes} timeZone={timeZone} />
          <Fact term="Last notification">{when(health.last_notification_at, timeZone, "None yet")}</Fact>
          <Fact term="Last missed notification">{when(health.last_missed_at, timeZone, "None")}</Fact>
          <Fact term="Waiting to be read">
            {health.waiting === 0
              ? "None"
              : `${health.waiting.toLocaleString("en-US")} ${health.waiting === 1 ? "notification" : "notifications"}: read as they arrive, and every 5 minutes`}
          </Fact>
          <Fact term="Looked for missed email">
            {health.read_back === null
              ? "Not run yet"
              : `${when(health.read_back.last_success_at, timeZone)}${
                  health.read_back.last_success_at === null || health.read_back_emails === null
                    ? ""
                    : ` · ${String(health.read_back_emails)} ${health.read_back_emails === 1 ? "email" : "emails"} read into days`
                }`}
          </Fact>
          <LastError run={health.read_back} timeZone={timeZone} term="Missed email error" />
          <Fact term="Nightly check">
            {health.reconcile === null ? "Not run yet" : when(health.reconcile.last_success_at, timeZone)}
          </Fact>
          <LastError run={health.reconcile} timeZone={timeZone} term="Nightly check error" />
          <EmailFacts email={health.email} timeZone={timeZone} />
        </>
      ) : (
        <Fact term="Status">
          Microsoft 365 isn&apos;t set up in this environment: the worker has never signed in to it (m365.md step 6).
        </Fact>
      )}
    </IntegrationPanel>
  );
}

// Where the last backup went: ADR 0015's exit codes 0, 3 and 2.
const LANDED_TEXT: Record<NonNullable<BackupHealth["landed"]>, string> = {
  nas: "Yes",
  this_host: "No: kept on the Docker host only",
  none: "No: nothing was written",
};

const size = formatBytes;

/**
 * The last copy of a backup to the owner's S3-compatible storage (ADR 0204):
 * when, which and where it went; or, after a failure, why and when the last
 * one went. The server warns when one fails or none has gone in 26 hours.
 */
function OffsiteCopy({ health, timeZone }: { health: BackupHealth; timeZone: string }) {
  if (!health.offsite_set_up) {
    return <>Not set up: BACKUP_S3_BUCKET isn&apos;t set on the stack.</>;
  }
  const run = health.offsite?.run ?? null;
  if (run === null) {
    return <>None yet</>;
  }
  if (run.last_error !== null && run.last_error_at !== null) {
    return (
      <>
        {`Failed ${formatDateTimeIn(run.last_error_at, timeZone)}: `}
        <span className="font-mono text-[12.5px] break-words">{run.last_error}</span>
        {` · last copied ${when(run.last_success_at, timeZone, "never")}`}
      </>
    );
  }
  const file = health.offsite?.file;
  const bucket = health.offsite?.bucket;
  return (
    <>
      {when(run.last_success_at, timeZone, "None yet")}
      {file ? (
        <>
          {" · "}
          <span className="font-mono text-[12.5px] break-all">{file}</span>
        </>
      ) : null}
      {bucket ? ` · to ${bucket}` : null}
    </>
  );
}

/**
 * The backup container's last backup (ADR 0090; ADR 0003 section 2): when,
 * whether it reached the NAS, and the newest that did. The server warns when
 * that is over 26 hours old.
 */
function Backups({
  health,
  timeZone,
}: {
  health: BackupHealth;
  timeZone: string;
}) {
  const { run } = health;
  return (
    <IntegrationPanel
      id="health-backups"
      name="Backups"
      level={health.level}
      problems={health.problems}
    >
      {health.level === "not_set_up" ? (
        <Fact term="Status">
          Backups aren&apos;t set up here: no backup container runs in this
          environment.
        </Fact>
      ) : (
        <>
          <Fact term="Last backup">
            {run?.last_attempt_at
              ? `${formatDateTimeIn(run.last_attempt_at, timeZone)}${health.kind ? ` · ${health.kind}` : ""}`
              : "None recorded yet"}
          </Fact>
          {health.landed !== null ? (
            <Fact term="Reached the NAS">{LANDED_TEXT[health.landed]}</Fact>
          ) : null}
          <Fact term="Newest on the NAS">
            {when(run?.last_success_at ?? null, timeZone, "None yet")}
          </Fact>
          {health.file !== null ? (
            <Fact term="File">
              <span className="font-mono text-[12.5px] break-all">
                {health.file}
              </span>
              {health.size_bytes === null
                ? null
                : ` · ${size(health.size_bytes)}`}
            </Fact>
          ) : null}
          <LastError run={run} timeZone={timeZone} term="Last backup error" />
          <Fact term="Offsite copy">
            <OffsiteCopy health={health} timeZone={timeZone} />
          </Fact>
          {health.disk === null ? null : (
            <>
              {health.disk.database_bytes === null ? null : (
                <Fact term="Database">{size(health.disk.database_bytes)}</Fact>
              )}
              <Fact term="Docker host's disk">
                {`${size(health.disk.free_bytes)} free of ${size(health.disk.size_bytes)} · ` +
                  `${Math.round(health.disk.used_pct).toString()}% used · read ${formatDateTimeIn(health.disk.read_at, timeZone)}`}
              </Fact>
            </>
          )}
        </>
      )}
    </IntegrationPanel>
  );
}

/**
 * SKIP_PREDEPLOY_BACKUP, the owner's override for one deploy (#842): set on
 * the stack, deploys take no backup first. Said above the cards as well as on
 * the Backups card, which is failing while it's set: it's meant to be deleted
 * once that deploy is done.
 */
function SkippedPredeployBackup() {
  return (
    <p role="alert" className="flex items-center gap-2 px-1 text-[13px] font-medium text-status-needs-client-fg">
      <span aria-hidden="true" className="size-2.5 shrink-0 rounded-pill bg-now" />
      SKIP_PREDEPLOY_BACKUP is set: deploys take no backup first. Delete it from the stack, and
      redeploy, once the deploy it was for is done.
    </p>
  );
}

function count(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * The office SonicWall's syslog (roadmap PR-79; ADRs 0158 and 0163): the
 * receiver's last message, what it kept and dropped today, whether the
 * SonicWall sends connection events at all, and what's matched against.
 * VPN work is the VPN card's (ADR 0180).
 */
function Network({ health, timeZone }: { health: NetworkHealth; timeZone: string }) {
  // The receiver's day: today, unless it hasn't reported since midnight.
  const day = health.day !== null && health.day !== todayIn(timeZone) ? `${formatLongDay(health.day)}: ` : "";
  return (
    <IntegrationPanel id="network-health" name="Network" level={health.level} problems={health.problems}>
      <Fact term="Last message">{when(health.last_message_at, timeZone, "Nothing yet")}</Fact>
      <Fact term="Today">
        {`${day}${count(health.received)} received · ${count(health.kept)} kept · ${count(health.dropped)} dropped · ${count(health.lost)} lost`}
      </Fact>
      <Fact term="What arrived">
        {`${count(health.opened)} Connection Opened · ${count(health.closed)} Connection Closed · ${count(health.other)} other${
          health.unreadable > 0 ? ` · ${count(health.unreadable)} unreadable` : ""
        }`}
      </Fact>
      <Fact term="Last connection events">
        {`Opened ${when(health.last_opened_at, timeZone, "never")} · Closed ${when(health.last_closed_at, timeZone, "never")}`}
      </Fact>
      <Fact term="Last kept">{when(health.last_kept_at, timeZone, "None yet")}</Fact>
      <Fact term="Refused">
        {health.refused === 0
          ? "None"
          : `${count(health.refused)}${health.last_refused_from ? `, the last from ${health.last_refused_from}` : ""}`}
      </Fact>
      <Fact term="Waiting for the worker">
        {health.waiting === 0
          ? "None"
          : `${count(health.waiting)}, the oldest since ${when(health.oldest_waiting_at, timeZone)}`}
      </Fact>
      <Fact term="Matched against">
        {`${count(health.office_addresses)} office ${health.office_addresses === 1 ? "address" : "addresses"} · ${count(
          health.client_addresses,
        )} client ${health.client_addresses === 1 ? "address" : "addresses"} · ${
          health.ports.length === 0 ? "no ports" : `ports ${health.ports.join(", ")}`
        }`}
      </Fact>
    </IntegrationPanel>
  );
}

/** "3 tunnel lines", "1 tunnel line". */
function counted(n: number, one: string, many: string): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/**
 * VPN work (roadmap PR-86; ADR 0180): the Pritunl recorder's last message and
 * status, what the receiver kept and dropped of its lines today (ADR 0178),
 * the tunnels, the work that waits, and whose each VPN address is (ADR 0179).
 * A recorder gone quiet, and each flagged address, are among the problems.
 * Shown only once the stack runs the recorder, or one has been heard from.
 */
function Vpn({ health, timeZone }: { health: VpnHealth; timeZone: string }) {
  // The receiver's day: today, unless it hasn't reported since midnight.
  const day = health.day !== null && health.day !== todayIn(timeZone) ? `${formatLongDay(health.day)}: ` : "";
  const reasons = Object.entries(health.dropped_by)
    .filter(([, n]) => n > 0)
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b));
  return (
    <IntegrationPanel id="vpn-health" name="VPN" level={health.level} problems={health.problems}>
      <Fact term="Last message">{when(health.last_message_at, timeZone, "Nothing yet")}</Fact>
      <Fact term="Recorder">
        {health.status_at === null
          ? "No status line yet"
          : `${health.recorder ?? "Release not said"} · status ${when(health.status_at, timeZone)}${
              health.restarts ? ` · conntrack restarted ${counted(health.restarts, "time", "times")}` : ""
            }`}
      </Fact>
      <Fact term="Pritunl's VPN">
        {`${health.networks.length === 0 ? "no networks" : `networks ${health.networks.join(", ")}`} · ${
          health.udp_ports.length === 0 ? "no tunnel ports" : `tunnel ports ${health.udp_ports.join(", ")}`
        }`}
      </Fact>
      <Fact term="Today">
        {`${day}${counted(health.messages, "message", "messages")} · ${counted(health.work, "work line", "work lines")} kept · ${counted(
          health.tunnel_lines,
          "tunnel line",
          "tunnel lines",
        )} kept · ${count(health.dropped)} dropped · ${count(health.lost)} lost`}
      </Fact>
      <Fact term="Dropped, by why">
        {reasons.length === 0 ? "None" : reasons.map(([reason, n]) => `${count(n)} ${reason}`).join(" · ")}
      </Fact>
      <Fact term="Undelivered">
        {`${count(health.undelivered)} sent and never arrived · ${count(health.held)} held for the recorder's first status line`}
      </Fact>
      <Fact term="Last kept">
        {`Work ${when(health.last_work_at, timeZone, "never")} · Tunnel ${when(health.last_tunnel_at, timeZone, "never")}`}
      </Fact>
      <Fact term="Tunnels">
        {`${count(health.tunnels_up)} up now · ${counted(health.tunnels_today, "tunnel", "tunnels")} today, ${counted(
          health.people_today,
          "person",
          "people",
        )}`}
      </Fact>
      <Fact term="Waiting for the worker">
        {health.waiting === 0
          ? "None"
          : `${count(health.waiting)}, the oldest since ${when(health.oldest_waiting_at, timeZone)}`}
      </Fact>
      <Fact term="Credited to no one">
        {health.uncredited === 0
          ? "None"
          : `${counted(health.uncredited, "kept line", "kept lines")}: their VPN address isn't settled (Mappings → Techs)`}
      </Fact>
      <Fact term="VPN addresses">
        {`${count(health.learned)} learned · ${count(health.unsettled)} not yet${
          health.conflicts.length > 0 ? ` · ${count(health.conflicts.length)} to settle (Mappings → Techs)` : ""
        }`}
      </Fact>
    </IntegrationPanel>
  );
}

// procrastinate's statuses of a job, in words.
const JOB_STATUS_TEXT: Record<string, string> = {
  todo: "waiting since",
  doing: "running since",
  succeeded: "succeeded",
  failed: "failed",
  cancelled: "cancelled",
  aborted: "stopped",
};

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function housekeepingText(run: WorkerHealth["housekeeping"][number], timeZone: string): string {
  const name = capitalized(run.name);
  if (run.status === null) {
    return `${name}: not run yet`;
  }
  const latest = `${name}: ${JOB_STATUS_TEXT[run.status] ?? run.status} ${when(run.at, timeZone)}`;
  if (run.status === "succeeded") {
    return latest;
  }
  return run.last_success_at === null
    ? `${latest} (never succeeded)`
    : `${latest} (last succeeded ${when(run.last_success_at, timeZone)})`;
}

function workerRunning(health: WorkerHealth, timeZone: string): string {
  if (health.lanes_running > 0) {
    return `${count(health.lanes_running)} ${health.lanes_running === 1 ? "lane" : "lanes"} checked in at ${when(health.last_heartbeat_at, timeZone)}`;
  }
  if (health.last_heartbeat_at !== null) {
    return `No lane has checked in since ${when(health.last_heartbeat_at, timeZone)}`;
  }
  return health.level === "not_run" ? "No worker has run here yet" : "No lane is checked in";
}

/**
 * The worker itself (#847; ADR 0208), from procrastinate's own tables: its
 * lanes checking in, the last job it finished, the jobs that failed today
 * and those waiting, and its own housekeeping, which no vendor's card shows.
 */
function Worker({ health, timeZone }: { health: WorkerHealth; timeZone: string }) {
  return (
    <IntegrationPanel id="health-worker" name="Worker" level={health.level} problems={health.problems}>
      <Fact term="Running">{workerRunning(health, timeZone)}</Fact>
      <Fact term="Last job finished">{when(health.last_finished_at, timeZone, "None yet")}</Fact>
      <Fact term="Failed in the last 24 hours">
        {health.failed.length === 0 ? (
          "None"
        ) : (
          <ul>
            {health.failed.map((failed) => (
              <li key={failed.task}>{`${failed.task}: ${count(failed.count)}, the last ${when(failed.last_at, timeZone)}${failed.card ? ` (on the ${failed.card} card)` : ""}`}</li>
            ))}
          </ul>
        )}
      </Fact>
      <Fact term="Waiting to start">
        {health.waiting === 0
          ? "None"
          : `${count(health.waiting)}, the longest since ${when(health.oldest_waiting_since, timeZone)}`}
      </Fact>
      <Fact term="Housekeeping">
        <ul>
          {health.housekeeping.map((run) => (
            <li key={run.task}>{housekeepingText(run, timeZone)}</li>
          ))}
        </ul>
      </Fact>
      {health.retention === null ? null : (
        <Fact term="Payload retention's last run">
          {`${when(health.retention.at, timeZone)}: ${count(health.retention.pruned)} records deleted, ${count(health.retention.kept)} kept${health.retention.more ? ", the rest the next night" : ""}`}
        </Fact>
      )}
    </IntegrationPanel>
  );
}

/**
 * The release this stack runs (ADR 0168): its stack file's, which its
 * Repository reference names, and its images'. They differ only while
 * IMAGE_TAG, set in Portainer, overrides the reference.
 */
function Release({ release }: { release: NonNullable<Integrations["release"]> }) {
  if (release.images === release.stack_file) {
    return <p className="px-1 text-[13px] text-muted">{`Release ${release.images}`}</p>;
  }
  return (
    <p role="alert" className="px-1 text-[13px] font-medium text-status-needs-client-fg">
      {`This stack runs ${possessive(release.images)} images, but its Repository reference is ${release.stack_file}: ` +
        "IMAGE_TAG, set in Portainer, overrides it. Unless you set it on purpose, delete IMAGE_TAG from the " +
        "stack and redeploy."}
    </p>
  );
}

/**
 * The worker's heartbeat to an outside monitor (ADR 0202): whether
 * HEARTBEAT_URL is set, and the last ping. Not a card: the monitor, which
 * runs elsewhere, is what tells you when the pings stop. Never the address,
 * which holds the check's token: only its host.
 */
function Heartbeat({ heartbeat, timeZone }: { heartbeat: HeartbeatHealth; timeZone: string }) {
  if (!heartbeat.set_up) {
    return (
      <p className="px-1 text-[13px] text-muted">
        No heartbeat to an outside monitor: HEARTBEAT_URL isn&apos;t set on the worker.
      </p>
    );
  }
  const to = `Heartbeat to ${heartbeat.host ?? "the monitor"}`;
  const run = heartbeat.run;
  if (run?.last_attempt_at == null) {
    return <p className="px-1 text-[13px] text-muted">{`${to}: no ping yet.`}</p>;
  }
  if (run.last_error !== null && run.last_error_at !== null) {
    return (
      <p role="alert" className="px-1 text-[13px] font-medium text-status-needs-client-fg">
        {`${to}: the last ping, ${formatDateTimeIn(run.last_error_at, timeZone)}, failed: ${run.last_error}. ` +
          `Last taken: ${when(run.last_success_at, timeZone)}.`}
      </p>
    );
  }
  return (
    <p className="px-1 text-[13px] text-muted">{`${to}: last ping taken ${when(run.last_success_at, timeZone)}.`}</p>
  );
}

export function IntegrationHealth() {
  const { api, me } = useSession();
  const query = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.getIntegrations(),
    refetchInterval: REFRESH_MS,
    // Once for a server error; a refusal (4xx) won't pass on a second try.
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) && failures < 1,
  });

  if (query.data === undefined) {
    if (query.isError) {
      const error = query.error;
      return (
        <Panel role="alert">
          <p className="font-semibold">Couldn&apos;t load integration health</p>
          <p className="mt-1 text-muted">
            {error instanceof ApiError
              ? (error.detail ?? error.message)
              : error.message}
          </p>
        </Panel>
      );
    }
    return (
      <Panel
        role="status"
        aria-label="Loading integration health"
        className="h-48 motion-safe:animate-pulse"
      />
    );
  }
  const { data } = query;
  return (
    <>
      {query.isError ? (
        // Old results, not current ones (#253): as the day view says (ADR 0032).
        <p
          role="status"
          className="px-1 text-[13px] font-medium text-status-needs-client-fg"
        >
          {`Couldn't refresh: this is what was checked at ${formatDateTimeIn(data.checked_at, me.timezone)}. Trying again every minute.`}
        </p>
      ) : (
        <p className="px-1 text-[13px] text-muted">{`Checked ${formatDateTimeIn(data.checked_at, me.timezone)}`}</p>
      )}
      {data.release ? <Release release={data.release} /> : null}
      {data.backups?.skip_predeploy_backup ? <SkippedPredeployBackup /> : null}
      {data.heartbeat ? <Heartbeat heartbeat={data.heartbeat} timeZone={me.timezone} /> : null}
      <div className="grid items-start gap-3 min-[1100px]:grid-cols-2">
        <Autotask health={data.autotask} timeZone={me.timezone} />
        <SkySwitch health={data.skyswitch} timeZone={me.timezone} />
        {data.screenconnect ? (
          <ScreenConnect health={data.screenconnect} timeZone={me.timezone} />
        ) : null}
        {data.datto_rmm ? (
          <DattoRmm health={data.datto_rmm} timeZone={me.timezone} />
        ) : null}
        {data.microsoft_365 ? (
          <Microsoft365 health={data.microsoft_365} timeZone={me.timezone} />
        ) : null}
        {data.backups ? (
          <Backups health={data.backups} timeZone={me.timezone} />
        ) : null}
        {data.network ? <Network health={data.network} timeZone={me.timezone} /> : null}
        {data.vpn ? <Vpn health={data.vpn} timeZone={me.timezone} /> : null}
        {data.worker ? <Worker health={data.worker} timeZone={me.timezone} /> : null}
      </div>
    </>
  );
}
